<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="`Questions &amp; Answers for &quot;${productVariant ? productVariant.name : &quot;Loading...&quot;}&quot;`"
              subtitle="Customer questions and seller/admin answers for this product variant"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn
                      color="grey darken-1"
                      outlined
                      small
                      @click="goBackToVariants"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Product Variants
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <v-card-text class="px-4 pb-4 pt-3">
              <v-row>
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Questions"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    hide-details
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-comment-question-outline"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20">mdi-table</v-icon>
                    <span class="text-body-1 font-weight-medium">Questions List</span>
                  </div>
                </v-col>
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="primary" outlined x-small>
                      <v-icon left x-small>mdi-comment-question-outline</v-icon>
                      {{ count }} {{ count === 1 ? "Question" : "Questions" }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-card-text class="pa-0">
              <section
                v-if="isLoading"
                style="height: 300px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading questions..." />
                </div>
              </section>

              <v-data-table
                v-else
                :headers="headers"
                :items="questions"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <template #[`item.question`]="{ item }">
                  <div class="text-body-2">{{ item.question }}</div>
                </template>

                <template #[`item.asked_by`]="{ item }">
                  <span class="text-caption">
                    {{ askedByName(item) }}
                  </span>
                </template>

                <template #[`item.status`]="{ item }">
                  <status-chip x-small :color="item.status === 1 ? 'success' : 'warning'" :icon="item.status === 1 ? 'mdi-check-circle' : 'mdi-clock-outline'" :label="String(item.status === 1 ? 'Answered' : 'Pending')" />
                </template>

                <template #[`item.answers`]="{ item }">
                  <div v-if="item.product_answers && item.product_answers.length">
                    <div
                      v-for="answer in item.product_answers"
                      :key="answer.id"
                      class="text-caption mb-1"
                    >
                      <span class="font-weight-medium">{{ answeredByName(answer) }}:</span>
                      {{ answer.answer }}
                      <span class="grey--text ml-1">
                        (<v-icon x-small>mdi-thumb-up-outline</v-icon> {{ answer.like_count || 0 }}
                        <v-icon x-small class="ml-1">mdi-thumb-down-outline</v-icon> {{ answer.dislike_count || 0 }})
                      </span>
                    </div>
                  </div>
                  <span v-else class="grey--text text-caption">No answer yet</span>
                </template>

                <template #[`item.created_at`]="{ item }">
                  <span class="text-caption grey--text">{{ formatDate(item.created_at) }}</span>
                </template>

                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">mdi-comment-question-outline</v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Questions Found</h3>
                    <p class="grey--text mb-4">
                      No customers have asked questions about this product variant yet.
                    </p>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <v-col cols="12" md="3" class="pa-0">
                  <span class="text-caption grey--text">
                    {{ (page - 1) * itemsPerPage + 1 }}-{{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                  </span>
                </v-col>
                <v-col cols="12" md="9" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn x-small outlined :disabled="page <= 1" @click="page--" class="mr-2">
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>
                    <v-btn x-small outlined :disabled="page >= paginationLength" @click="page++" class="ml-2">
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "ProductQuestionsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "",
      },
      page: 1,
      itemsPerPage: 10,
      count: 0,
      paginationLength: 0,
      questions: [],
      productVariant: null,
      isLoading: false,
      statuses: [
        { text: "Pending", value: "0" },
        { text: "Answered", value: "1" },
      ],
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px", align: "center" },
        { text: "Question", value: "question", sortable: false, width: "260px" },
        { text: "Asked By", value: "asked_by", sortable: false, width: "140px" },
        { text: "Status", value: "status", sortable: false, width: "110px" },
        { text: "Answers", value: "answers", sortable: false, width: "320px" },
        { text: "Asked On", value: "created_at", sortable: false, width: "120px" },
      ],
    };
  },

  watch: {
    page: "getData",
  },

  mounted() {
    this.getProductVariant();
    this.getData();
  },

  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const productVariantId = this.decryptIt(this.$route.params.productVariantId);
        const params = new URLSearchParams({
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          product_variant_id: productVariantId,
          search_keyword: this.filter.search_keyword || "",
          status: this.filter.status || "",
        });
        const response = await axios.get(`/product_questions?${params}`);
        this.questions = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Failed to load product questions",
          });
        }
      }
      this.isLoading = false;
    },

    async getProductVariant() {
      try {
        const productVariantId = this.$route.params.productVariantId;
        const response = await axios.get(`/product_variants/${productVariantId}?context=panel`);
        if (response.data.success) {
          this.productVariant = response.data.data;
        }
      } catch (error) {
        console.log("Error loading product variant:", error);
      }
    },

    goBackToVariants() {
      this.$router.push(`/products/${this.$route.params.productId}/product-variants`);
    },

    askedByName(item) {
      if (item.user && (item.user.first_name || item.user.last_name)) {
        return `${item.user.first_name || ""} ${item.user.last_name || ""}`.trim();
      }
      return "Unknown User";
    },

    answeredByName(answer) {
      if (answer.user && (answer.user.first_name || answer.user.last_name)) {
        return `${answer.user.first_name || ""} ${answer.user.last_name || ""}`.trim();
      }
      return "Support Team";
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.v-container {
  max-width: 100% !important;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}
</style>
